/* eslint-disable jsx-a11y/control-has-associated-label */
import {
  useEffect, useMemo, useRef, useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Fuse from "fuse.js";
import { IconBuildingBank, IconChevronLeft } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { capitalizeFirstLetter } from "src/utils";
import { ReactComponent as IconError } from "src/assets/icons/toast-error.svg";
import { ReactComponent as IconSuccess } from "src/assets/icons/toast-success.svg";
import { Box, Text, Button as MantineButton } from "@mantine/core";
import useCleverTapEvents from "src/events/useGetEvents";
import { BANK_DETAILS_EVENT } from "src/events/constant";
import { ScreenWrapper } from "../../components";
import { RootState } from "../../redux/store";
import { ErrorFallback, popularBanksList } from "../../utils/constants";
import {
  clearBankDetailsFormError,
  getBankBranches,
  getBanks,
  saveBankAccountDetails,
  setBankDetailsFormStatus,
} from "./bankDetailsFormSlice";
import { useStyles } from "./linkBankAccount.styles";
import { ReactComponent as SearchIcon } from "../../assets/icons/magnify.svg";
import { ReactComponent as CloseIcon } from "../../assets/icons/close-circle-outline.svg";
import { BankAccountVerifyLoader } from "./containers/BankAccountVerifyLoader";
import { ConfirmBankModal } from "./containers/confirmBankModal";
import { ScreenLoader } from "../../components/screenLoader";
import { TextInput, Button } from "../../components/atoms";
import manualUseStyles from "./manualFlow.styles";

type BankListItemType = {
  code: string;
  name: string;
};

type BankBranchListItemType = {
  address: string;
  city: string;
  code: string;
  district: string;
  ifsc: string;
  name: string;
  state: string;
};

const ManualFlow = ({ handleSelectFlow } : { handleSelectFlow : (flow: 'pennyDrop' | 'manual' | 'null') => void }) => {
  const { classes, cx } = useStyles();
  const { classes: manualClasses } = manualUseStyles();
  const [selectedBank, setSelectedBank] = useState<BankListItemType | null>(
    null
  );
  const [selectedBankBranch, setSelectedBankBranch] = useState<BankBranchListItemType | null>(null);
  const bankDetailsState = useSelector((state: RootState) => state.bankDetails);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [bankSearchString, setBankSearchString] = useState("");
  const [branchSearchString, setBranchSearchString] = useState("");
  const [isBankDetailsModalOpen, setIsBankDetailsModalOpen] = useState(false);
  const [acNumber, setAcNumber] = useState("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const transferLoader = selectedBank?.name
    && selectedBankBranch?.name
    && bankDetailsState.status === "loading";
  const acInputRef = useRef<HTMLInputElement>(null);
  const userDetails = useSelector((state:RootState) => state.account.userDetails)
  const { TriggerEvent } = useCleverTapEvents();
  const isPopularBank = popularBanksList.find((item) => item.code === selectedBank?.code);

  useEffect(() => {
    dispatch(getBanks());
  }, []);

  useEffect(() => {
    if (selectedBank?.code) {
      dispatch(
        getBankBranches(`${selectedBank?.code}?q=${branchSearchString}`)
      );
    }
  }, [branchSearchString]);

  useEffect(() => {
    if (bankDetailsState.status === "success") {
      dispatch(setBankDetailsFormStatus("idle"));
      notifications.show({
        color: 'green',
        message: 'Bank Details Saved!',
        icon: <IconSuccess />,
      });
      navigate("/verify-image");
    } else if (bankDetailsState.status === "error") {
      TriggerEvent(BANK_DETAILS_EVENT.bankError, {
        bankName: selectedBank?.name,
        popular: isPopularBank !== undefined,
        errorMessage: bankDetailsState.error.message || "",
      })
      dispatch(clearBankDetailsFormError());
      notifications.show({
        color: 'red',
        message: capitalizeFirstLetter(bankDetailsState.error.message) || ErrorFallback,
        icon: <IconError />,
      });
      setErrorMessage(
        capitalizeFirstLetter(bankDetailsState.error.message) || ErrorFallback
      );
    }
  }, [bankDetailsState.status]);

  const sortedBanksData = bankDetailsState.banks.length > 0 ? bankDetailsState.banks : [];
  const sortedBankBranchesData = bankDetailsState.bankBranches.length > 0
    ? bankDetailsState.bankBranches
    : [];

  const options = {
    keys: ["name", "code"],
  };
  const fuse = useMemo(() => new Fuse(sortedBanksData, options), [sortedBanksData]);
  const banksResult = fuse.search(bankSearchString, { limit: 10 });

  const onNextClick = () => {
    dispatch(
      saveBankAccountDetails({
        beneficiaryAccount: acNumber,
        beneficiaryIFSC: selectedBankBranch?.ifsc,
        beneficiaryName: userDetails.name,
        beneficiaryAccountType: "savings",
      })
    );
    TriggerEvent(BANK_DETAILS_EVENT.bankSubmit, {
      name: userDetails.name,
      bankName: selectedBank?.name,
      popular: isPopularBank !== undefined,
    })
  };

  const selectBank = (bank: any) => {
    setSelectedBank(bank);
    dispatch(getBankBranches(`${bank.code}?q=`));
  };

  const selectBankBranch = (branch: any) => {
    setSelectedBankBranch(branch);
    setIsBankDetailsModalOpen(true);
  };

  const shortenAddress = (addr: string) => {
    if (selectedBank) {
      return addr
        .replace(`${selectedBank?.name?.toUpperCase()} LTD`, "")
        .replace(",", " ")
        .replace(".", " ")
        .slice(0, 34);
    }
    return addr
      .replace("", "")
      .replace(",", " ")
      .replace(".", " ")
      .slice(0, 34);
  };

  const onSearchBank = (e: any) => {
    setBankSearchString(e.target.value);
  };

  const onSearchBranch = (e: any) => {
    setBranchSearchString(e.target.value);
  };

  const onChangeAcNumber = (e: any) => {
    let num = e.target.value;
    if (num.length < 17) {
      num = num.replace(/[^0-9]{1,10}/g, "")?.replace(/(\..*?)\..*/g, "$1");
      setAcNumber(num);
    }
    setErrorMessage("");
  };

  const onDeselectBank = () => {
    setSelectedBank(null);
    setSelectedBankBranch(null);
    setAcNumber("");
    setBankSearchString("");
    setBranchSearchString("");
  };

  const onDeselectBranch = () => {
    setIsBankDetailsModalOpen(false);
    setSelectedBankBranch(null);
    setAcNumber("");
    setBranchSearchString("");
  };

  const onBankDetailsModalClose = () => {
    setIsBankDetailsModalOpen(false);
    acInputRef.current?.focus();
  };

  const renderHeader = () => {
    if (selectedBank) {
      return (
        <div className={classes.mobileInputWrapper}>
          <div className={classes.mobileInput}>
            <div className={classes.selectedSearchTag}>
              <p>{selectedBank?.name}</p>
              <button
                type="button"
                className={classes.selectedSearchTagClose}
                onClick={onDeselectBank}
              >
                <CloseIcon />
              </button>
            </div>
          </div>
          <div className={classes.inputLeftIcon}>
            <SearchIcon />
          </div>
        </div>
      );
    }
    return (
      <TextInput
        type="search"
        placeholder="Search Bank Name"
        name="search bank"
        id="search_bank"
        title="search bank"
        onChange={onSearchBank}
        value={bankSearchString}
        icon={<SearchIcon />}
      />
    );
  };

  const renderSelectBranchContainer = () => (
    <>
      <h2 className={classes.heading}>Search for your bank&apos;s IFSC</h2>
      {selectedBankBranch ? (
        <div className={cx(classes.selectBranchContainer, classes.transBack)}>
          <div className={cx(classes.mobileInputWrapper, classes.mb14)}>
            <div className={classes.mobileInput}>
              <div className={classes.selectedSearchTag}>
                <p className={classes.mr10}>
                  {`${selectedBankBranch.ifsc}, ${selectedBankBranch.city}`}
                </p>
                <button
                  type="button"
                  className={classes.selectedSearchTagClose}
                  onClick={onDeselectBranch}
                >
                  <CloseIcon />
                </button>
              </div>
            </div>
            <div className={classes.inputLeftIcon}>
              <SearchIcon />
            </div>
          </div>
          <h2 className={classes.headingAccNumber}>
            Enter your account number
          </h2>
          <div className={classes.textInputWrapper}>
            <TextInput
              type="text"
              placeholder="Your bank account number "
              name="Enter bank account number"
              id="account_number_otp"
              title="Enter bank account number"
              value={acNumber}
              onChange={onChangeAcNumber}
              error={errorMessage}
            />
          </div>

          <p className={classes.acNumberInputDesc}>
            {"We will transfer "}
            <b>₹1</b>
            {" to your account to verify."}
          </p>
        </div>
      ) : (
        <div className={classes.selectBranchContainer}>
          <div className={classes.mobileTextInputWrapper}>
            <TextInput
              maxLength={12}
              type="search"
              placeholder="Your IFSC or Branch"
              name="ifsc search"
              id="ifsc_search"
              title="search branch using IFSC"
              onChange={onSearchBranch}
              value={branchSearchString}
              className={classes.isfcSearchbarInput}
            />
          </div>
          {sortedBankBranchesData.map((item: BankBranchListItemType) => (
            <button
              type="button"
              key={item.ifsc}
              className={classes.bankSearchCard}
              onClick={() => {
                selectBankBranch(item);
              }}
            >
              <IconBuildingBank />
              <div>
                <p className={classes.bankSearchCardText}>
                  {`${item.city} - ${item.ifsc}`}
                </p>
                <p className={classes.bankSearchCardSubtext}>
                  {shortenAddress(item.address).length >= 34
                    ? `${shortenAddress(item.address)}...`
                    : shortenAddress(item.address)}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
    </>
  );

  const handleBackClick = () => handleSelectFlow("null")

  return (
    <ScreenWrapper>
      <div className={manualClasses.container}>
        <div className={classes.header}>{renderHeader()}</div>
        <div className={manualClasses.contentContainer}>
          {selectedBank ? (
            renderSelectBranchContainer()
          ) : (
            <>
              <Text pt="sm" className={classes.heading}>Select your bank</Text>
              {!bankSearchString?.length ? (
                <>
                  <p className={classes.bankCategory}>POPULAR BANKS</p>
                  <div className={classes.popularBankList}>
                    {popularBanksList.map((item) => (
                      <button
                        type="button"
                        key={item.code}
                        className={cx(
                          classes.bankSearchCard,
                          classes.popularBankSearchCard
                        )}
                        onClick={() => {
                          selectBank(item);
                        }}
                      >
                        <IconBuildingBank />
                        <p className={classes.bankSearchCardText}>
                          {item.name}
                        </p>
                      </button>
                    ))}
                  </div>
                  <p className={classes.bankCategory}>ALL BANKS</p>
                </>
              ) : null}

              <div className={classes.allBankList}>
                {bankSearchString?.length < 1
                  ? sortedBanksData.map((item: any) => (
                    <button
                      type="button"
                      key={item.code}
                      className={classes.bankSearchCard}
                      onClick={() => {
                        selectBank(item);
                      }}
                    >
                      <IconBuildingBank />

                      <p className={classes.bankSearchCardText}>
                        {item.name}
                      </p>
                    </button>
                  ))
                  : banksResult.map(({ item }: any) => (
                    <button
                      type="button"
                      key={item.code}
                      className={classes.bankSearchCard}
                      onClick={() => {
                        selectBank(item);
                      }}
                    >
                      <IconBuildingBank />
                      <p className={classes.bankSearchCardText}>
                        {item.name}
                      </p>
                    </button>
                  ))}
              </div>
            </>
          )}
        </div>
        <Box className={manualClasses.footerContainer}>
          <MantineButton
            radius="md"
            className="heading_2xs_semibold"
            variant="outline"
            onClick={handleBackClick}
            leftIcon={<IconChevronLeft />}
          >
            Back
          </MantineButton>
          <Button onClick={onNextClick}>
            Next
          </Button>
        </Box>
        {transferLoader && <BankAccountVerifyLoader />}
        <ConfirmBankModal
          opened={isBankDetailsModalOpen}
          onClose={onBankDetailsModalClose}
          selectedBankBranch={selectedBankBranch}
          onPrimaryBtnClick={onBankDetailsModalClose}
          onSecondaryBtnClick={onDeselectBranch}
        />
      </div>
      {bankDetailsState.status === "loading" ? <ScreenLoader /> : null}
    </ScreenWrapper>
  );
};

export default ManualFlow;
